import { Activity, ArrowUpRight, BadgeCheck, Compass, HeartHandshake, MapPin, Percent, TrendingUp, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { BUSINESS_EMAIL } from "@/lib/site";

const CAN_DO = [
  {
    icon: BadgeCheck,
    title: "Claim your bar",
    body: "Verify ownership and take control of your venue's profile — hours, photos, and what tonight looks like.",
  },
  {
    icon: Percent,
    title: "Post specials in real time",
    body: "Happy hours and deals go live to nearby users the moment you publish them — right when they're deciding where to go.",
  },
  {
    icon: Compass,
    title: "Get discovered",
    body: "Show up in venue discovery, sorted closest-first for people who are already out and looking.",
  },
  {
    icon: HeartHandshake,
    title: "Reward the regulars",
    body: "See the crews who keep pulling up and give them a reason to make your spot the default.",
  },
];

const INSIGHTS = [
  { icon: Activity, label: "Live check-ins", body: "How many people are at your spot right now" },
  { icon: MapPin, label: "Pull-up activity", body: "Plans that started nearby and chose you" },
  { icon: TrendingUp, label: "Peak nights", body: "The hours and days your venue trends" },
  { icon: Users, label: "Regulars & crews", body: "Repeat visits, crew sizes, who brings friends" },
];

export const Business = () => (
  <section id="business" data-testid="business-section" className="py-20 sm:py-28 px-6">
    <div className="max-w-6xl mx-auto">
      <Reveal className="text-center">
        <p className="text-[11px] font-semibold tracking-[0.25em] text-[#4FE3E0] uppercase">
          Pull Up for Business
        </p>
        <h2 className="mt-4 font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
          Your bar, on the map when it matters
        </h2>
        <p className="mt-4 text-base text-zinc-400 max-w-2xl mx-auto">
          Claim your bar, post specials, and reward the regulars who pull up — then watch
          exactly what the night did for you.
        </p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-2 gap-5">
        <div className="grid sm:grid-cols-2 gap-5">
          {CAN_DO.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 0.06}>
                <article
                  data-testid={`business-feature-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group h-full rounded-2xl bg-[#121816] border border-white/10 p-6 hover:border-[#4FE3E0]/35 hover:bg-[#151C1A] transition-colors duration-300"
                >
                  <Icon className="w-6 h-6 text-[#4FE3E0] transition-transform duration-300 group-hover:scale-110" strokeWidth={2.2} />
                  <h3 className="mt-4 font-semibold text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{c.body}</p>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.12}>
          <div className="h-full rounded-2xl bg-[#121816] border border-[#4FE3E0]/25 p-7 sm:p-8 flex flex-col" data-testid="business-insights-card">
            <p className="text-[11px] font-semibold tracking-[0.25em] text-[#4FE3E0] uppercase">
              The insights you get
            </p>
            <div className="mt-6 space-y-5 flex-1">
              {INSIGHTS.map((ins) => {
                const Icon = ins.icon;
                return (
                  <div key={ins.label} className="flex items-start gap-4">
                    <span className="w-10 h-10 shrink-0 rounded-xl bg-[#4FE3E0]/10 border border-[#4FE3E0]/25 flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-[#4FE3E0]" />
                    </span>
                    <div>
                      <p className="font-semibold text-white text-sm">{ins.label}</p>
                      <p className="text-sm text-zinc-400 mt-0.5">{ins.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <a
              href={BUSINESS_EMAIL}
              data-testid="business-contact-button"
              className="group mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-[#4FE3E0] text-black font-semibold text-sm px-6 py-3.5 hover:bg-[#F4F4F0] transition-colors duration-300"
            >
              Talk to us about Pull Up for Business
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
